import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * <ExpandableText /> — Wave 26.3.
 *
 * Truncates long text to N lines on mobile (and any narrow surface),
 * reveals the full text on tap. Wraps the existing render so we don't
 * touch every consumer's prose styling — just drop it around the
 * original `<p>` content.
 *
 * Designed for JC task comments / brief / description on mobile, where
 * the existing layout was clipping comments to half a line.
 *
 * Props:
 *   text       — required, the string content
 *   lines      — collapsed line count (default 3)
 *   className  — wrapper class
 *   textClassName — class applied to the text node
 *   testId     — data-testid root
 *   alwaysExpand — bypass the collapse (used for desktop fallback)
 *   children   — optional trailing slot (e.g. a ReadAloud button)
 */
export default function ExpandableText({
  text,
  lines = 3,
  className = "",
  textClassName = "whitespace-pre-wrap text-[#dde3ec]",
  testId = "expandable-text",
  alwaysExpand = false,
  children = null,
}) {
  const ref = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Decide whether to show "Read more". We let the browser render
  // clamped, then compare scrollHeight to clientHeight.
  useEffect(() => {
    if (alwaysExpand || !ref.current) {
      setOverflows(false);
      return undefined;
    }
    const el = ref.current;
    const measure = () => {
      const o = el.scrollHeight > el.clientHeight + 1;
      setOverflows(o);
    };
    measure();
    // Re-measure on resize (mobile rotation, dev tools toggle).
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => ro?.disconnect();
  }, [text, lines, alwaysExpand]);

  const content = String(text || "");
  if (!content.trim()) return null;

  const clamped = !alwaysExpand && !expanded;
  const style = clamped
    ? {
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }
    : {};

  return (
    <div className={className} data-testid={testId}>
      <div ref={ref} className={textClassName} style={style}>
        {content}
      </div>
      <div className="flex items-center gap-2 mt-1">
        {(overflows || expanded) && !alwaysExpand && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className="text-[10px] uppercase tracking-wider text-[#1FB6C1] hover:text-[#22d6e3] inline-flex items-center gap-0.5"
            data-testid={`${testId}-toggle`}
          >
            {expanded ? (
              <>
                Show less <ChevronUp className="w-3 h-3" />
              </>
            ) : (
              <>
                Read more <ChevronDown className="w-3 h-3" />
              </>
            )}
          </button>
        )}
        {children}
      </div>
    </div>
  );
}
